import usePrivateAxios from "@/common/api/usePrivateAxios";
import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { UserValidationSchema, userSchema } from "@/common/schemas/create.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Path, SubmitHandler, useForm } from "react-hook-form";
import SelectController from "@/common/components/SelectController";
import { ImageUploader } from "@/common/components/ImageUploader";
import { useState } from "react";
import MaskedTextField from "@/common/components/MaskedTextField";
import { FilterOptionOption } from "node_modules/react-select/dist/declarations/src/filters";
import useDebounce from "@/common/hooks/useDebounce";
import { filterOptions } from "../../constants/filterOptions.constant";
import { statusOptions } from "../../constants/statusOptions.constant";
import { phoneMask } from "@/common/constants/masks.constant";
import { useGeosOptions } from "@/common/hooks/queries/useGeosOptions";
import { checkIsImage } from "@/common/utils/fileValidator";
import { transformNewRoles } from "@/common/utils/roleTransformers";
import { useRoles } from "@/common/hooks/queries/useRoles";
import { Mapper } from "@/common/services/mapper";
import { RoleData } from "@/common/types/roles";
import { FormTransformers } from "@/common/utils/formTransformers";
import { removeSpecialCharacters } from "@/common/utils/unFormatters";
import { GeoTransformers } from "@/common/services/geoTransformers";
import useCpfDetails from "@/common/hooks/queries/useCpfDetails";
import { ImportDetailsMapper } from "@/common/services/importDetailsMapper";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { AxiosError, AxiosResponse } from "axios";
import GoBackButton from "@/common/components/GoBackButton";
import { authenticationMethodOptions, authenticationMethods } from "@/features/users/constants/authenticationMethodOptions.constant";

const Create = () => {
  const navigate = useNavigate();
  const privateAxios = usePrivateAxios();
  const [file, setFile] = useState<Blob>();
  const [geoSearch, setGeoSearch] = useDebounce('');
  const [searchRole, setSearchRole] = useDebounce('');

  const [userId, setUserId] = useState('');
  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const { data: rolesOptions } = useRoles({
    filters: { search: searchRole },
    mapper: Mapper.mapToOptions<RoleData>({ labelFieldName: 'display_name', valueFieldName: 'id' })
  });

  const geos = useGeosOptions(geoSearch);

  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors }
  } = useForm<UserValidationSchema>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      roles: [],
    },
  });

  const { mutate: createUser, isPending: createUserIsPending } = useMutation({
    mutationKey: ['createUser'],
    mutationFn: async (form: UserValidationSchema) => {
      const payload = {
        ...form,
        cpf: form.cpf ? removeSpecialCharacters(form.cpf) : null,
        user_roles_attributes: form?.roles?.length ? form.roles.map(transformNewRoles) : [],
        status: form?.status?.value || 'enabled',
        geo_geoables_attributes: form?.geos?.map((geo) => ({ geo_id: geo.value, geo_type: geo.type })),
        phone_attributes: form?.phone?.number ? {
          number: form?.phone?.number ? removeSpecialCharacters(form?.phone?.number) : null
        } : undefined,
        profile_picture: file ? file : undefined,
        sicof_user_identification: form.sicof_user_identification || null,
        authentication_method: form.authentication_method?.value || null,
      };

      return privateAxios.post('/users', { user: payload }, {
        headers: {
          'content-type': "multipart/form-data"
        }
      });
    },
    onSuccess: (response: AxiosResponse, form: UserValidationSchema) => {
      if (response.data.data.id) {
        setUserId(response.data.data.id);
      }

      setIsError(false);

      if (form.authentication_method.value === authenticationMethods.password) {
        setMessages(['Senha enviada ao usuário por e-mail e/ou SMS.']);
      } else {
        setMessages(['Usuário criado com sucesso.']);
      }
    },
    onError: (error: AxiosError) => {
      FormTransformers.errorsTransformer<UserValidationSchema>({ setError })(error);
    },
  });

  const onSubmit: SubmitHandler<UserValidationSchema> = (data) => createUser(data);

  const { mutate: importData, isPending: importDataIsPending } = useCpfDetails({
    onSuccess: (response) => {
      clearErrors('cpf');

      ImportDetailsMapper.cpfDetails()(response.data.user).forEach(([field, value]) => {
        clearErrors(field as Path<UserValidationSchema>);
        setValue(field as Path<UserValidationSchema>, value);
      });
    },
    onError: (error) => {
      setError('cpf', { message: error?.response?.data?.errors?.base?.[0] });
    },
  });

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-4 items-center">
            <GoBackButton
              fallback="/users"
              searchLastRouteFromFallback
            />
            <h2 className="text-2xl font-bold">Novo usuário</h2>
          </div>
          <Button
            name="submit"
            type="submit"
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            disabled={createUserIsPending}
            isLoading={createUserIsPending}
          >
            Confirmar
          </Button>
        </div>
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] h-[calc((100vh-40px-4rem-1.5rem))] overflow-y-auto">
          <p className="text-lg font-bold">Dados pessoais</p>
          <ImageUploader
            isLoading={false}
            label="Foto"
            name="profile-picture"
            accept="image/*"
            onChange={(e) => {
              const file = (e.target as HTMLInputElement & { files: FileList; }).files?.[0];
              if (!file || !checkIsImage(file)) {
                return;
              }
              setFile((e.target as HTMLInputElement & { files: FileList; }).files[0]);
            }}
            currentImageUrl={file ? URL.createObjectURL(file) : undefined}
            disabled={createUserIsPending}
          />
          <div className="flex gap-2 w-full justify-between">
            <div className="w-3/4">
              <MaskedTextField
                label="CPF"
                mask='000.000.000-00'
                errorMessage={errors?.cpf?.message || undefined}
                isLoading={importDataIsPending}
                {...register('cpf')}
                disabled={createUserIsPending}
              />
              {importDataIsPending ? (
                <p className="text-sm text-blue-400">Importando dados</p>
              ) : (
                <Button variant='link' onClick={() => importData(watch('cpf'))} disabled={createUserIsPending}>Importar dados</Button>
              )}
            </div>
            <MaskedTextField
              label="Data de nascimento"
              mask='00/00/0000'
              className="w-1/4"
              errorMessage={errors.date_of_birth?.message || undefined}
              {...register('date_of_birth')}
              disabled={createUserIsPending}
              markAsRequired
            />
          </div>
          <div className="flex gap-2 w-full justify-between">
            <TextField
              label="Nome"
              maxLength={64}
              className="w-3/4"
              errorMessage={errors.full_name?.message || undefined}
              {...register('full_name')}
              disabled={createUserIsPending}
              markAsRequired
            />
            <SelectController
              control={control}
              name="roles"
              label="Perfil"
              className="w-1/4"
              options={rolesOptions || []}
              onInputChange={setSearchRole}
              isMulti
              markAsRequired
              isDisabled={createUserIsPending}
              errorMessage={errors.roles?.message as string || undefined}
            />
          </div>
          <SelectController
            control={control}
            name="status"
            label="Status"
            options={statusOptions}
            isDisabled={createUserIsPending}
          />
          <SelectController
            control={control}
            name="geos"
            label="Região de atuação"
            isMulti
            onInputChange={setGeoSearch}
            filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
            options={GeoTransformers.mapToGeoOptions()(geos)}
            isDisabled={createUserIsPending}
          />
          <SelectController
            control={control}
            name="authentication_method"
            label="Configuração de login"
            options={authenticationMethodOptions}
            errorMessage={errors.authentication_method?.message || undefined}
            isDisabled={createUserIsPending}
            markAsRequired
          />
          <MaskedTextField
            label="Telefone"
            mask={phoneMask}
            errorMessage={errors.phone?.number?.message || undefined}
            {...register('phone.number')}
            disabled={createUserIsPending}
          />
          <TextField
            label="Email"
            {...register('email')}
            errorMessage={errors.email?.message || undefined}
            disabled={createUserIsPending}
            markAsRequired
          />
          <MaskedTextField
            label="Código SICOF"
            mask='000'
            {...register('sicof_user_identification')}
            errorMessage={errors.sicof_user_identification?.message || undefined}
            disabled={createUserIsPending}
          />
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
          onClose={() => navigate(`/users/${userId}`)}
        />
      )}
    </>
  );
};

export default Create;
